import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import { useAuth } from "../../context/authContext";
import {
  UseRegisterAuditionMutation,
  UseAuditionEnrollMutation,
  useAuditionStatus,
  useDropdown,
  useStatesDropdowns,
  UsecitiesDropdowns,
  useUser,
} from "../../actions/authActions";

export default () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loadingToapi, setLoadingToapi] = useState(false);
  const [countryCode, setCountryCode] = useState(null);
  const [cca2, setCca2] = useState(null);

  const userId = localStorage.getItem("userId");
  const isLoggedIn = !!userId;
 
  const {
    data: userprofile,
    isLoading: isLoadingPrefilled,
  } = useUser(userId);
  console.log("userprofile", userprofile, "useUser(userId)", useUser(userId))
  const { data: auditionStatusData, isLoading: isLoadingAuditionStatus } =
    useAuditionStatus(isLoggedIn);

  const hasSubmission =
    auditionStatusData?.has_submission === true ||
    auditionStatusData?.has_submission === "true";

  // Only true once the status API has actually confirmed a logged-in user has no prior submission
  const isEnrollMode =
    isLoggedIn &&
    !isLoadingAuditionStatus &&
    !!auditionStatusData &&
    !hasSubmission;

  useEffect(() => {
    if (isLoggedIn && !isLoadingAuditionStatus && hasSubmission) {
      navigate("/gem-audition-status");
    }
  }, [isLoggedIn, isLoadingAuditionStatus, hasSubmission, navigate]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      mobile_num: "",
      date_of_birth: "",
      hide_dob_from_front_end: false,
      email: "",
      password: "",
      gender_id: "",
      country_id: "",
      state_id: "",
      city_id: "",
      agree_to_vote_to_win: false,
      terms_of_use: true,
      referral_code: "",
      event_code: "",
    },
  });

  useEffect(() => {
    if (
      !isLoadingPrefilled &&
      userprofile &&
      Object.keys(userprofile).length > 0
    ) {
      setValue("first_name", userprofile?.first_name || "");
      setValue("last_name", userprofile?.last_name || "");
      setValue("email", userprofile?.email || "");
      setValue("mobile_num", userprofile?.mobile_num || "");
      setValue(
        "gender_id",
        userprofile?.gender_id ? String(userprofile.gender_id) : ""
      );
      if (userprofile?.date_of_birth) {
        const parsedDob = new Date(userprofile.date_of_birth);
        if (!isNaN(parsedDob)) {
          setValue("date_of_birth", parsedDob);
        }
      }
      setValue(
        "country_id",
        userprofile?.country_id ? String(userprofile.country_id) : ""
      );
      setValue(
        "state_id",
        userprofile?.state_id ? String(userprofile.state_id) : ""
      );
      setValue(
        "city_id",
        userprofile?.city_id ? String(userprofile.city_id) : ""
      );
      setValue(
        "agree_to_vote_to_win",
        userprofile?.agree_to_vote_to_win === 1
      );
      setValue("referral_code", userprofile?.referral_code || "");
      // Already agreed during the original account registration
      setValue("terms_of_use", true);
      if (userprofile?.country_code) {
        setCountryCode(userprofile.country_code);
      }
      if (userprofile?.cca2) {
        setCca2(userprofile.cca2);
      }
    }
  }, [userprofile, isLoadingPrefilled, setValue]);

  const { data: dropdowns, isLoading: isDropdownLoading, isError, error } =
    useDropdown();

  const selectedCountry = watch("country_id");
  const selectedState = watch("state_id");
  const { data: states, isLoading: isLoadingStates } =
    useStatesDropdowns(selectedCountry);
  const { data: cities, isLoading: isLoadingCities } =
    UsecitiesDropdowns(selectedState);

  const proofOfPayment = watch("proof_of_payment");
  const video = watch("video");
  const proofOfPaymentFile = proofOfPayment?.[0];
  const videoFile = video?.[0];

  const [proofOfPaymentPreview, setProofOfPaymentPreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);

  useEffect(() => {
    if (!proofOfPaymentFile) {
      setProofOfPaymentPreview(null);
      return;
    }
    const url = URL.createObjectURL(proofOfPaymentFile);
    setProofOfPaymentPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [proofOfPaymentFile]);

  useEffect(() => {
    if (!videoFile) {
      setVideoPreview(null);
      return;
    }
    const url = URL.createObjectURL(videoFile);
    setVideoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [videoFile]);

  const registerMutation = UseRegisterAuditionMutation(
    setLoadingToapi,
    navigate,
    login
  );
  const enrollMutation = UseAuditionEnrollMutation(setLoadingToapi, navigate);

  const onSubmit = (data, e) => {
    e.preventDefault();

    Swal.fire({
      title: "Submit your GEM Audition?",
      text: "Please confirm the details you've entered are correct before submitting.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d9a239",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      setLoadingToapi(true);

      const formattedDob = data.date_of_birth
        ? new Date(data.date_of_birth).toISOString().split("T")[0]
        : "";

      // Logged-in user with no prior submission: only the audition-specific fields are editable/submitted
      if (isLoggedIn) {
        const enrollPayload = new FormData();
        enrollPayload.append("video", data.video?.[0]);
        enrollPayload.append("event_code", data.event_code);
        enrollPayload.append("proof_of_payment", data.proof_of_payment?.[0]);
        enrollPayload.append("date_of_birth", formattedDob);
        enrollPayload.append("gender_id", data.gender_id);
        enrollPayload.append("country_id", data.country_id);
        enrollPayload.append("state_id", data.state_id);
        enrollPayload.append("city_id", data.city_id);
        
        enrollPayload.append("qualification", data.qualification);
        enrollPayload.append("acting_experience", data.experience);
        enrollPayload.append("hair_color", data.hair_color);
        enrollPayload.append("eye_color", data.eye_color);
        enrollPayload.append("skin_tone", data.skin_tone);
        enrollPayload.append("body_type", data.body_type);
        enrollPayload.append("height_cm", data.height);
        enrollPayload.append("weight_kg", data.weight);

        enrollMutation.mutate(enrollPayload);
        return;
      }

      // Guest: full registration
      let phoneNumber = data.mobile_num || "";
      let dialCode = countryCode;
      if (phoneNumber.startsWith("+")) {
        const match = phoneNumber.match(/^(\+\d+)\s*(.+)$/);
        if (match) {
          dialCode = match[1];
          phoneNumber = match[2];
        }
      }

      const payload = new FormData();
      payload.append("first_name", data.first_name);
      payload.append("last_name", data.last_name);
      payload.append("country_code", dialCode || "");
      payload.append("cca2", cca2 || "");
      payload.append("mobile_num", phoneNumber);
      payload.append("date_of_birth", formattedDob);
      payload.append(
        "hide_dob_from_front_end",
        data.hide_dob_from_front_end ? "1" : "0"
      );
      payload.append("email", data.email);
      payload.append("password", data.password);
      payload.append("password_confirmation", data.password);
      payload.append("proof_of_payment", data.proof_of_payment?.[0]);
      payload.append("video", data.video?.[0]);
      payload.append("gender_id", data.gender_id);
      payload.append("country_id", data.country_id);
      payload.append("state_id", data.state_id);
      payload.append("city_id", data.city_id);
      payload.append("username_type", "email");

      payload.append("qualification", data.qualification);
      payload.append("acting_experience", data.experience);
      payload.append("hair_color", data.hair_color);
      payload.append("eye_color", data.eye_color);
      payload.append("skin_tone", data.skin_tone);
      payload.append("body_type", data.body_type);
      payload.append("height_cm", data.height);
      payload.append("weight_kg", data.weight);


      payload.append(
        "agree_to_vote_to_win",
        data.agree_to_vote_to_win ? "1" : "0"
      );
      payload.append("terms_of_use", data.termsAndConditions ? "1" : "0");
      payload.append("referral_code", data.referral_code);
      payload.append("event_code", data.event_code);
      console.log("payload", payload)
      registerMutation.mutate(payload);
    });
  };

  return {
    register,
    navigate,
    handleSubmit,
    setValue,
    control,
    watch,
    errors,
    onSubmit,
    loadingToapi,
    setCountryCode,
    setCca2,
    userprofile,
    isLoadingPrefilled,
    isLoggedIn,
    isLoadingAuditionStatus,
    hasSubmission,
    isEnrollMode,
    dropdowns,
    isDropdownLoading,
    isError,
    error,
    states,
    isLoadingStates,
    cities,
    isLoadingCities,
    proofOfPayment,
    video,
    proofOfPaymentPreview,
    videoPreview,
  };
};
